// External modules
import React from 'react';

// Internal modules
import { useWindowSize } from '@/hooks';
import { ThemeProvider } from '@emotion/react';
import { PAYPAL_THEME } from '@paypalcorp/pp-react-theme-weasley-v7';
import { Dialog, Sheet } from '@paypalcorp/pp-react-modal-weasley-v7';
import type { AtomicEventsAttributes } from '@/lib/atomics';

type HybridInterstitialProps = {
  children: JSX.Element;
  closeOnBackgroundClick?: boolean;
  footerContents?: JSX.Element;
  hideTitle?: boolean;
  isOpen?: boolean;
  onClose: () => void;
  testId?: string;
  title: string;
  closeButtonAtomicEvents?: AtomicEventsAttributes;
};

/*
  This component is a combination of the PP-React Sheet & Dialog Component.
  When screen size is equal to or less than 751 pixels it uses the
  Sheet component. When screen size is greater than 751 pixels it uses
  the Dialog component.
*/
const HybridInterstitial = ({
  children,
  closeOnBackgroundClick = true,
  footerContents,
  hideTitle = true,
  isOpen = true,
  onClose,
  testId,
  title,
  closeButtonAtomicEvents,
}: HybridInterstitialProps): JSX.Element => {
  const closeButtonProps = {
    'data-atomic-wait-domain': closeButtonAtomicEvents?.domain,
    'data-atomic-wait-flow': closeButtonAtomicEvents?.flow,
    'data-atomic-wait-intent': closeButtonAtomicEvents?.intent,
    'data-atomic-wait-task': closeButtonAtomicEvents?.task,
    'data-atomic-wait-viewname': closeButtonAtomicEvents?.viewName,
    'data-testid': 'close-interstitial-button',
  };
  // Hooks
  const { isMobile } = useWindowSize();

  const Component = isMobile ? Sheet : Dialog;

  return (
    <ThemeProvider theme={PAYPAL_THEME}>
      <Component
        center
        closeButtonProps={closeButtonProps}
        closeOnBackgroundClick={closeOnBackgroundClick}
        footerContents={footerContents}
        hideTitle={hideTitle}
        isOpen={isOpen}
        onCloseComplete={onClose}
        data-testid={testId}
        title={title}
      >
        {children}
      </Component>
    </ThemeProvider>
  );
};

export { HybridInterstitial };
